/**
 * US state registry for LookBel's national location URL hierarchy.
 *
 * Authored as CommonJS on purpose: this module is consumed both by the CRA
 * webpack bundle (via `import`) and by the Node build-time prerender/sitemap
 * generator (via `require`). Keeping one copy avoids drift between the HTML a
 * crawler sees and the HTML the SPA renders.
 *
 * Covering all 50 states + DC here is a *routing/data* capability, not a
 * publishing decision. Whether a given state page is indexable is decided
 * separately by `eligibility.js` from real stylist inventory.
 */

/** @type {Array<{ code: string, name: string, slug: string }>} */
const US_STATES = [
  { code: 'AL', name: 'Alabama', slug: 'alabama' },
  { code: 'AK', name: 'Alaska', slug: 'alaska' },
  { code: 'AZ', name: 'Arizona', slug: 'arizona' },
  { code: 'AR', name: 'Arkansas', slug: 'arkansas' },
  { code: 'CA', name: 'California', slug: 'california' },
  { code: 'CO', name: 'Colorado', slug: 'colorado' },
  { code: 'CT', name: 'Connecticut', slug: 'connecticut' },
  { code: 'DE', name: 'Delaware', slug: 'delaware' },
  { code: 'DC', name: 'District of Columbia', slug: 'district-of-columbia' },
  { code: 'FL', name: 'Florida', slug: 'florida' },
  { code: 'GA', name: 'Georgia', slug: 'georgia' },
  { code: 'HI', name: 'Hawaii', slug: 'hawaii' },
  { code: 'ID', name: 'Idaho', slug: 'idaho' },
  { code: 'IL', name: 'Illinois', slug: 'illinois' },
  { code: 'IN', name: 'Indiana', slug: 'indiana' },
  { code: 'IA', name: 'Iowa', slug: 'iowa' },
  { code: 'KS', name: 'Kansas', slug: 'kansas' },
  { code: 'KY', name: 'Kentucky', slug: 'kentucky' },
  { code: 'LA', name: 'Louisiana', slug: 'louisiana' },
  { code: 'ME', name: 'Maine', slug: 'maine' },
  { code: 'MD', name: 'Maryland', slug: 'maryland' },
  { code: 'MA', name: 'Massachusetts', slug: 'massachusetts' },
  { code: 'MI', name: 'Michigan', slug: 'michigan' },
  { code: 'MN', name: 'Minnesota', slug: 'minnesota' },
  { code: 'MS', name: 'Mississippi', slug: 'mississippi' },
  { code: 'MO', name: 'Missouri', slug: 'missouri' },
  { code: 'MT', name: 'Montana', slug: 'montana' },
  { code: 'NE', name: 'Nebraska', slug: 'nebraska' },
  { code: 'NV', name: 'Nevada', slug: 'nevada' },
  { code: 'NH', name: 'New Hampshire', slug: 'new-hampshire' },
  { code: 'NJ', name: 'New Jersey', slug: 'new-jersey' },
  { code: 'NM', name: 'New Mexico', slug: 'new-mexico' },
  { code: 'NY', name: 'New York', slug: 'new-york' },
  { code: 'NC', name: 'North Carolina', slug: 'north-carolina' },
  { code: 'ND', name: 'North Dakota', slug: 'north-dakota' },
  { code: 'OH', name: 'Ohio', slug: 'ohio' },
  { code: 'OK', name: 'Oklahoma', slug: 'oklahoma' },
  { code: 'OR', name: 'Oregon', slug: 'oregon' },
  { code: 'PA', name: 'Pennsylvania', slug: 'pennsylvania' },
  { code: 'RI', name: 'Rhode Island', slug: 'rhode-island' },
  { code: 'SC', name: 'South Carolina', slug: 'south-carolina' },
  { code: 'SD', name: 'South Dakota', slug: 'south-dakota' },
  { code: 'TN', name: 'Tennessee', slug: 'tennessee' },
  { code: 'TX', name: 'Texas', slug: 'texas' },
  { code: 'UT', name: 'Utah', slug: 'utah' },
  { code: 'VT', name: 'Vermont', slug: 'vermont' },
  { code: 'VA', name: 'Virginia', slug: 'virginia' },
  { code: 'WA', name: 'Washington', slug: 'washington' },
  { code: 'WV', name: 'West Virginia', slug: 'west-virginia' },
  { code: 'WI', name: 'Wisconsin', slug: 'wisconsin' },
  { code: 'WY', name: 'Wyoming', slug: 'wyoming' },
];

const STATE_BY_SLUG = new Map();
const STATE_BY_CODE = new Map();
const STATE_BY_NAME = new Map();

US_STATES.forEach((state) => {
  STATE_BY_SLUG.set(state.slug, state);
  STATE_BY_CODE.set(state.code, state);
  STATE_BY_NAME.set(state.name.toLowerCase(), state);
});

/**
 * Resolve any of: URL slug ("north-carolina"), postal code ("NC"),
 * or full name ("North Carolina") to the canonical state record.
 *
 * @param {string | null | undefined} value
 * @returns {{ code: string, name: string, slug: string } | null}
 */
function resolveState(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();
  if (STATE_BY_SLUG.has(lower)) return STATE_BY_SLUG.get(lower);
  if (STATE_BY_CODE.has(raw.toUpperCase())) return STATE_BY_CODE.get(raw.toUpperCase());
  if (STATE_BY_NAME.has(lower)) return STATE_BY_NAME.get(lower);

  // Tolerate "north carolina" typed with underscores/extra whitespace.
  const normalized = lower.replace(/[\s_]+/g, '-');
  if (STATE_BY_SLUG.has(normalized)) return STATE_BY_SLUG.get(normalized);

  return null;
}

/**
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
function isKnownState(value) {
  return resolveState(value) !== null;
}

module.exports = {
  US_STATES,
  resolveState,
  isKnownState,
};
